import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MapContext } from 'os-elements';

const PANEL_BUFFER = 44;
const EDGE_BUFFER = 10;

/**
 * Hook to manage the position of a floating panel anchored to a map coordinate.
 *
 * The panel tracks a given map coordinate (the active point), converting it to
 * pixel position on each render/move/zoom. If the user drags the panel, a manual
 * offset is applied. Clamping ensures the panel stays within the map viewport.
 *
 * @param {object} options
 * @param {import('ol/coordinate').Coordinate|null} options.anchorCoordinate - The map coordinate to anchor to.
 * @param {React.RefObject} options.panelRef - Ref to the panel DOM element.
 * @param {React.RefObject} options.containerRef - Ref to the map container element.
 * @returns {{ position: {left: number, top: number}, isDragging: boolean, handleDragStart: Function }}
 */
const useFloatingPanelPosition = ({ anchorCoordinate, panelRef, containerRef }) => {
    const { map } = useContext(MapContext);
    const [position, setPosition] = useState({ left: -9999, top: -9999 });
    const [isDragging, setIsDragging] = useState(false);

    // Manual offset from the anchor pixel (set when user drags the panel)
    const manualOffsetRef = useRef(null);
    // Starting position before a drag, used to snap back on off-screen drop
    const dragStartPosRef = useRef(null);
    // Mouse offset within the panel when drag started
    const dragMouseOffsetRef = useRef({ x: 0, y: 0 });

    /**
     * Clamp panel position so it stays fully inside the container.
     */
    const clampToContainer = useCallback((left, top) => {
        if (!panelRef.current || !containerRef.current) {
            return { left, top };
        }

        const containerRect = containerRef.current.getBoundingClientRect();
        const panelRect = panelRef.current.getBoundingClientRect();
        const panelWidth = panelRect.width;
        const panelHeight = panelRect.height;

        const minLeft = EDGE_BUFFER;
        const minTop = EDGE_BUFFER;
        const maxLeft = containerRect.width - panelWidth - EDGE_BUFFER;
        const maxTop = containerRect.height - panelHeight - EDGE_BUFFER;

        return {
            left: Math.max(minLeft, Math.min(left, maxLeft)),
            top: Math.max(minTop, Math.min(top, maxTop))
        };
    }, [panelRef, containerRef]);

    /**
     * Compute panel position from anchor coordinate.
     * If user has manually dragged (manualOffsetRef is set), apply that offset.
     * Otherwise, place with PANEL_BUFFER to the right of the anchor pixel.
     */
    const computePosition = useCallback(() => {
        if (!map || !anchorCoordinate || !panelRef.current || !containerRef.current) {
            return null;
        }

        const pixel = map.getPixelFromCoordinate(anchorCoordinate);
        if (!pixel) {
            return null;
        }

        let left;
        let top;

        if (manualOffsetRef.current) {
            left = pixel[0] + manualOffsetRef.current.x;
            top = pixel[1] + manualOffsetRef.current.y;
        } else {
            left = pixel[0] + PANEL_BUFFER;
            top = pixel[1] - (panelRef.current.offsetHeight / 2);
        }

        return clampToContainer(left, top);
    }, [map, anchorCoordinate, clampToContainer, panelRef, containerRef]);

    /**
     * Update panel position. Called on map postrender and when anchor changes.
     */
    const updatePosition = useCallback(() => {
        if (isDragging) {
            return;
        }
        const pos = computePosition();
        if (pos) {
            setPosition(pos);
        }
    }, [computePosition, isDragging]);

    // Reset manual offset when anchor coordinate changes (new point or active point switch)
    useEffect(() => {
        manualOffsetRef.current = null;
    }, [anchorCoordinate?.[0], anchorCoordinate?.[1]]);

    // Listen for map postrender to update position on pan/zoom
    useEffect(() => {
        if (!map) {
            return;
        }
        map.on('postrender', updatePosition);
        return () => {
            map.un('postrender', updatePosition);
        };
    }, [map, updatePosition]);

    // Update position immediately when anchor changes
    useEffect(() => {
        updatePosition();
    }, [updatePosition]);

    /**
     * Start drag from the drag handle.
     */
    const handleDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        if (!panelRef.current) {
            return;
        }

        const panelRect = panelRef.current.getBoundingClientRect();
        dragMouseOffsetRef.current = {
            x: clientX - panelRect.left,
            y: clientY - panelRect.top
        };

        dragStartPosRef.current = { ...position };
        setIsDragging(true);
    }, [position, panelRef]);

    /**
     * Handle drag move.
     */
    const handleDragMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) {
            return;
        }

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const containerRect = containerRef.current.getBoundingClientRect();

        const newLeft = clientX - containerRect.left - dragMouseOffsetRef.current.x;
        const newTop = clientY - containerRect.top - dragMouseOffsetRef.current.y;

        const clamped = clampToContainer(newLeft, newTop);
        setPosition(clamped);
    }, [isDragging, containerRef, clampToContainer]);

    /**
     * Handle drag end.
     */
    const handleDragEnd = useCallback(() => {
        if (!isDragging) {
            return;
        }

        setIsDragging(false);

        if (!panelRef.current || !containerRef.current) {
            return;
        }

        const panelRect = panelRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        // Check if any part of panel is off-screen — snap back to start position
        const isOffScreen =
            panelRect.left < containerRect.left ||
            panelRect.right > containerRect.right ||
            panelRect.top < containerRect.top ||
            panelRect.bottom > containerRect.bottom;

        if (isOffScreen && dragStartPosRef.current) {
            setPosition(dragStartPosRef.current);
            return;
        }

        // Store the manual offset relative to the anchor pixel
        if (map && anchorCoordinate) {
            const pixel = map.getPixelFromCoordinate(anchorCoordinate);
            if (pixel) {
                manualOffsetRef.current = {
                    x: position.left - pixel[0],
                    y: position.top - pixel[1]
                };
            }
        }
    }, [isDragging, map, anchorCoordinate, position, panelRef, containerRef]);

    // Attach global mouse/touch listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove, { passive: false });
            document.addEventListener('touchend', handleDragEnd);
        }
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('touchmove', handleDragMove);
            document.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    return { position, isDragging, handleDragStart };
};

export default useFloatingPanelPosition;
